import React from 'react';
import clsx from 'clsx';
import type { VirtuosoGridHandle } from 'react-virtuoso';
import { VirtuosoGrid } from 'react-virtuoso';
import title from 'title';
import { getManyFromList } from '@soundxyz/utils/helpers';
import { useSafeLayoutEffect } from '@components-shared/src/hooks/useSafeLayoutEffect';
import { LoadingGIF } from '@components/basics/LoadingGif';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import { useParams } from '@context/params';
import { globalSearchSoundState, useSearchInputNew } from '@hooks/search/useSearchInputNew';
import { GRID_STYLE } from '@utils/constants';
import { getNextTrackIds } from '@utils/helpers';
import { ExplorePageNoResults } from './ExplorePage/ExplorePageNoResults';
import { Filters } from './Filters';
import { MobileFilters } from './MobileFilters';
import { renderItemContent } from './SearchResults';
import { SearchResultsSkeleton } from './SearchResultsSkeleton';
import { ArtistsSort, CollectorsSort, PlaylistsSort, SoundsSort } from './Sort';
import { useCurrentSearchTabsAndFilters } from './helpers';
import { EXPLORE_TYPES, isSearchTab, SEARCH_TAB_CHOICES } from './schema';
import { setActiveTab } from './useSearchPageStore';
import { useSearchResults } from './useSearchResults';

const LOADING_MOBILE_ITEMS = 20;

const getSortDropdown = ({ exploreType }: { exploreType: string | null }) => {
  switch (exploreType) {
    case EXPLORE_TYPES.SOUNDS:
      return <SoundsSort />;
    case EXPLORE_TYPES.ARTISTS:
      return <ArtistsSort />;
    case EXPLORE_TYPES.COLLECTORS:
      return <CollectorsSort />;
    case EXPLORE_TYPES.PLAYLISTS:
      return <PlaylistsSort />;
    default:
      return null;
  }
};

export const ExploreResults = () => {
  const { authUser } = useAuth();
  const authUserId = authUser?.id;
  const { tab: paramActiveTab, explore_type } = useParams();

  const { showAvailabilityFilter, showLocationFilter, showGenreFilter } =
    useCurrentSearchTabsAndFilters();

  const {
    searchResults,
    loadMore,
    isInitialLoading: isLoading,
    isFetchingNextPage,
    hasNextPage = false,
  } = useSearchResults();

  const { searchPageText } = useSearchInputNew({
    searchState: globalSearchSoundState,
  });

  const { tablet } = useMediaQuery();

  const loadingNext = isFetchingNextPage && hasNextPage;

  const memoizedFooter = React.useMemo(() => (loadingNext ? <LoadingGIF /> : null), [loadingNext]);

  const gridRef = React.useRef<VirtuosoGridHandle>(null);

  const hasNoSearchResults = searchResults.length === 0;

  // When tab query changes, update local state powering the tab component
  useSafeLayoutEffect(() => {
    if (isSearchTab(paramActiveTab)) {
      return setActiveTab({
        activeTab: paramActiveTab,
      });
    }
  }, [paramActiveTab]);

  useSafeLayoutEffect(() => {
    if (tablet && searchPageText !== '') {
      if (paramActiveTab == null) {
        return setActiveTab({ activeTab: SEARCH_TAB_CHOICES.ALL });
      }
    }
  }, [tablet, searchPageText]);

  const sortDropdown = getSortDropdown({ exploreType: explore_type });

  return (
    <div
      className={clsx(
        'mx-auto my-0 flex min-h-screen flex-col px-4 md:gap-4',
        'md:gap-2 md:px-6 md:pb-4',
        'lg: max-w-7xl',
        loadingNext ? 'pb-28 md:pb-40' : 'pb-2 md:pb-4',
      )}
    >
      <h3 className="flex items-center justify-between font-title text-title-l font-medium">
        {title(explore_type ?? '')}
      </h3>
      <div className="flex justify-between">
        <div className="block md:hidden">
          <MobileFilters
            showAvailabilityFilter={showAvailabilityFilter}
            showLocationFilter={showLocationFilter}
            showGenreFilter={showGenreFilter}
          />
        </div>
        <div className="hidden md:block">
          <Filters
            showAvailabilityFilter={showAvailabilityFilter}
            showLocationFilter={showLocationFilter}
            showGenreFilter={showGenreFilter}
          />
        </div>
        {sortDropdown}
      </div>
      {isLoading ? (
        <div className="mt-2">
          <div className="hidden md:block">
            <SearchResultsSkeleton />
          </div>
          <div className="block md:hidden">
            <div className="grid grid-cols-2 gap-4 md:hidden md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {Array.from({ length: LOADING_MOBILE_ITEMS }).map((_, i) => (
                <div key={`top-results-mobile-${i}-skeleton`}>
                  <div className="h-12 w-full animate-pulse rounded-md bg-neutral200" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          {hasNoSearchResults ? (
            <ExplorePageNoResults />
          ) : (
            <div className="mt-2 md:mt-0" key={`search-results-${authUserId}}`}>
              <VirtuosoGrid
                ref={gridRef}
                context={{ loadMore }}
                endReached={loadMore}
                data={searchResults}
                useWindowScroll
                itemContent={(_, item) => {
                  return renderItemContent({
                    item,
                    getNextTrackIds:
                      item.__typename === 'Release'
                        ? getNextTrackIds({
                            trackId: item.track.id,
                            releases: getManyFromList(searchResults, edge => {
                              return edge.__typename === 'Release' ? edge : null;
                            }),
                          })
                        : undefined,
                  });
                }}
                overscan={100}
                listClassName={clsx('gap-4', GRID_STYLE)}
                components={{ Footer: () => memoizedFooter }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
